/**
 * Turns a content type's field declarations into a form — the requirement is
 * literal: "il form si generа DAI CAMPI DEL TIPO, non è scritto a mano".
 * Pure and framework-free on purpose, so it is unit-testable without
 * rendering anything (see `field-model.test.ts`).
 *
 * Path convention: `packages/contracts` only says a field path is a string
 * (`setField(cmd: Command<{ id: string; path: string; value: unknown }>)`),
 * and the one worked example in document/25-api-e-mcp.md §5
 * (`Localized/it/Meta/Title`) is slash-separated. Reading
 * `apps/gateway/src/routes/contents.ts` shows `content.state.data.title` used
 * directly — i.e. `data` is keyed flatly by the field's own key, with
 * locale already a property of the Content aggregate itself rather than a
 * nesting level inside `data`. So: a top-level field's path is simply its
 * key, and an item inside a "list of objects" field is
 * `<listKey>/<index>/<subKey>`, following the same slash convention. This is
 * the one guess this app makes about wire shape it could not confirm against
 * a live schema — see README.md "Decisioni prese".
 */
import type { FieldDefinition } from '@cms/contracts'

export const KNOWN_FIELD_TYPES = [
  'string',
  'text',
  'richtext',
  'number',
  'boolean',
  'enum',
  'date',
  'media',
  'reference',
  'list',
  'json',
] as const

export type KnownFieldType = (typeof KNOWN_FIELD_TYPES)[number]

/** Anything the type registry does not name yet is still editable — as raw JSON — never dropped. */
export function resolveControlType(field: FieldDefinition): KnownFieldType {
  // Case-insensitive: a type can declare `richText` (the provisioning does) and it is still the rich text control.
  const type = field.type.toLowerCase()
  if (type === 'object' || type === 'group') return 'json'
  return (KNOWN_FIELD_TYPES as readonly string[]).includes(type) ? (type as KnownFieldType) : 'json'
}

export interface FormFieldModel {
  readonly field: FieldDefinition
  readonly path: string
  readonly value: unknown
  readonly controlType: KnownFieldType
  readonly isKnownType: boolean
}

export interface ExtraFieldModel {
  readonly path: string
  readonly value: unknown
}

export interface FormModel {
  /** One entry per field declared by the content type, in the type's own order. */
  readonly known: readonly FormFieldModel[]
  /** Keys present in the document's data but not declared by the type — shown under "Altro", never discarded. */
  readonly extra: readonly ExtraFieldModel[]
}

export function pathForListItem(listKey: string, index: number, subKey: string): string {
  return `${listKey}/${index}/${subKey}`
}

export function buildFormModel(fields: readonly FieldDefinition[], data: Record<string, unknown>): FormModel {
  const known = fields.map(
    (field): FormFieldModel => ({
      field,
      path: field.key,
      value: data[field.key],
      controlType: resolveControlType(field),
      isKnownType: (KNOWN_FIELD_TYPES as readonly string[]).includes(field.type.toLowerCase()) || ['object', 'group'].includes(field.type.toLowerCase()),
    }),
  )

  const declaredKeys = new Set(fields.map((f) => f.key))
  const extra = Object.keys(data)
    .filter((key) => !declaredKeys.has(key))
    .map((key): ExtraFieldModel => ({ path: key, value: data[key] }))

  return { known, extra }
}

/** A protected field is visible but never editable from this form — the API enforces it too. */
export function isEditable(field: FieldDefinition): boolean {
  return field.protected !== true
}
